import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { customerFormSchema, type CustomerFormValues } from './customerSchema';
import { RowGroup, RowField } from '../../components/RowField';
import { StickyActionBar } from '../../components/StickyActionBar';
import { Button } from '../../components/Button';

interface CustomerFormProps {
  defaultValues?: Partial<CustomerFormValues>;
  submitLabel: string;
  onSubmit: (values: CustomerFormValues) => Promise<void> | void;
  onCancel?: () => void;
  submitting?: boolean;
}

const emptyDefaults: CustomerFormValues = {
  displayName: '',
  email: '',
  phone: '',
  preferredDeliveryChannel: 'Email',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  notes: ''
};

/**
 * Pack B2 Client form. Row-editor pattern: each field is a tappable row that
 * opens a focused Sheet editor. react-hook-form + zod still drive validation;
 * RowFields read `watch()` and commit via `setValue(…, { shouldValidate })`.
 */
export function CustomerForm({ defaultValues, submitLabel, onSubmit, onCancel, submitting }: CustomerFormProps) {
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: { ...emptyDefaults, ...defaultValues }
  });

  const v = watch();
  const busy = submitting || isSubmitting;
  const set = (k: keyof CustomerFormValues) => (val: string) => setValue(k, val, { shouldValidate: true });

  return (
    <form onSubmit={handleSubmit(async (values) => onSubmit(values))} className="space-y-5 pb-24 sm:pb-0">
      <RowGroup title="Contact">
        <RowField type="text" label="Display name" value={v.displayName} placeholder="Name" error={errors.displayName?.message} onCommit={set('displayName')} />
        <RowField type="text" label="Email" value={v.email ?? ''} placeholder="Add email" error={errors.email?.message} onCommit={set('email')} />
        <RowField type="text" label="Phone" value={v.phone ?? ''} placeholder="Add phone" error={errors.phone?.message} onCommit={set('phone')} />
        <RowField
          type="select"
          label="Preferred delivery"
          value={v.preferredDeliveryChannel}
          options={[
            { value: 'Email', label: 'Email' },
            { value: 'Sms', label: 'SMS (coming soon)' }
          ]}
          onCommit={(val) => setValue('preferredDeliveryChannel', val as CustomerFormValues['preferredDeliveryChannel'], { shouldValidate: true })}
        />
      </RowGroup>

      <RowGroup title="Address">
        <RowField type="text" label="Address line 1" value={v.addressLine1 ?? ''} placeholder="Add" onCommit={set('addressLine1')} />
        <RowField type="text" label="Address line 2" value={v.addressLine2 ?? ''} placeholder="Add" onCommit={set('addressLine2')} />
        <RowField type="text" label="City" value={v.city ?? ''} placeholder="Add" onCommit={set('city')} />
        <RowField type="text" label="Region/State" value={v.region ?? ''} placeholder="Add" onCommit={set('region')} />
        <RowField type="text" label="Postal code" value={v.postalCode ?? ''} placeholder="Add" onCommit={set('postalCode')} />
        <RowField type="text" label="Country" value={v.country ?? ''} placeholder="Add" onCommit={set('country')} />
      </RowGroup>

      <RowGroup title="Notes">
        <RowField type="multiline" label="Notes" value={v.notes ?? ''} placeholder="Add notes" onCommit={set('notes')} />
      </RowGroup>

      <StickyActionBar>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </StickyActionBar>
    </form>
  );
}
